import logError from 'src/data/services/logError';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import _ from 'lodash';
import { ApiService, Store } from 'src/data/services/Services';
import errorHandler from 'src/core/utils/errorHandler';
import Translations from 'src/core/utils/Translations';
import Constants from 'src/core/utils/Constants';
import type { ApiEvent } from 'src/core/models/api/ApiEvent';
import type { ApiPage } from 'src/core/models/api/ApiPage';
import { Keyboard } from 'react-native';
import { authEventBus } from 'src/core/events/Events';

import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useNotificationStore } from 'src/domain/stores/useNotificationStore';
import { useAnalyticsStore } from 'src/domain/stores/useAnalyticsStore';

const EventsStoreKey = Constants.storageEvents;

// Monotonic id for active-view fetches so a slow earlier one can't overwrite a newer one (rapid event
// switching).
let latestActiveFetchId = 0;

// Bumped on every local removal (delete/validate) so an in-flight background refresh cannot write a
// just-removed event back into storage (resurrection).
let eventsStoreMutation = 0;

// Request counter per event uuid, covering foreground and background fetches alike.
const latestFetchPerEvent: Record<string, number> = {};

interface EventStoreState {
	eventData: ApiEvent[];
	allPages: ApiPage[][];
	activeEventUuid: string;
	activeEventView: ApiPage[];
	isLoading: boolean;
	isAddingEvent: boolean;
	events: Record<string, unknown>;
	eventLogo: string | null;
	selectedEvents: string[];
	activeTheme: Record<string, string>;
	logoUrl: string | null;
	previousTheme: Record<string, string>;
	eventUuids: string;
	deviceOnline: boolean;
	pages: ApiPage[] | null;
	pendingEventCode: string | null;
	pendingAddEventIntent: boolean;
	pendingViewEventUuid: string | null;
	/** Timestamp (ms) of the last successful server fetch per event uuid, used to skip redundant refreshes. */
	lastFetchedAt: Record<string, number>;
}

const initialState: EventStoreState = {
	eventData: [],
	allPages: [],
	activeEventUuid: '',
	activeEventView: [],
	isLoading: false,
	isAddingEvent: false,
	events: {},
	eventLogo: null,
	selectedEvents: [],
	activeTheme: {},
	logoUrl: null,
	previousTheme: {},
	eventUuids: '',
	deviceOnline: true,
	pages: null,
	pendingEventCode: null,
	pendingAddEventIntent: false,
	pendingViewEventUuid: null,
	lastFetchedAt: {},
};

/**
 * The reasons the server refuses to add an event, each with its own message.
 */
function handleRedeemRefusal(reason: unknown, eventCode: string): boolean {
	if (reason === 'invalid code') {
		errorHandler(Translations.errorEventNotFoundTitle, Translations.errorEventNotFoundMessage);
		return true;
	}

	if (reason === 'already registered') {
		errorHandler(Translations.alertEventAlreadyAddedTitle, Translations.errorEventInList);
		return true;
	}

	if (reason === 'login required') {
		errorHandler(Translations.errorDefaultSubject, Translations.errorLoginRequiredForEvent);
		useEventStore.setState({ pendingEventCode: eventCode });
		Keyboard.dismiss();
		useNavigationStore.getState().navigate(Constants.screenLogin);
		return true;
	}

	return false;
}

export const useEventStore = create(
	combine(initialState, (set, get) => ({
		clearEventData: () => {
			set({
				eventData: [],
				allPages: [],
				activeEventUuid: '',
				activeEventView: [],
				events: {},
				eventLogo: null,
				selectedEvents: [],
				activeTheme: {},
				logoUrl: null,
				previousTheme: {},
				eventUuids: '',
				pages: null,
				lastFetchedAt: {},
			});
			useNotificationStore.setState({ eventUuids: '' });
		},

		setTheme: (theme: Record<string, string>) => set({ activeTheme: { ...theme } }),
		setLogo: (logoUrl: string | null) => set({ eventLogo: logoUrl }),
		setPendingAddEventIntent: (value: boolean) => set({ pendingAddEventIntent: value }),
		setPendingViewEventUuid: (value: string | null) => set({ pendingViewEventUuid: value }),
		setPendingEventCode: (code: string | null) => set({ pendingEventCode: code }),

		// Wist de lokaal opgeslagen events (geheugen + persistente storage).
		clearStoredEvents: () => {
			Store.remove(EventsStoreKey);
			useEventStore.getState().clearEventData();
		},

		init: async () => {
			const response = await Store.getObject(EventsStoreKey);
			if (response !== null) {
				const sortedEvents = _.orderBy(response as ApiEvent[], ['date'], ['desc']);
				const allPages = sortedEvents.map((event) => event.pages);
				const pages = _.flattenDeep(allPages) as ApiPage[];
				set({ eventData: sortedEvents, allPages, pages });
				useEventStore.getState().setEventUuids();
			}
		},

		updateTheme: (eventUuid: string) => {
			const { eventData, activeTheme } = get();
			const event = _.find(eventData, ['uuid', eventUuid]);
			if (event !== undefined) {
				set({ previousTheme: activeTheme, activeTheme: { ...event.theme.data } });
			}
		},

		resetTheme: () => {
			const { previousTheme } = get();
			set({ activeTheme: previousTheme, previousTheme: {} });
		},

		setEventUuids: (type?: string) => {
			const { eventData } = get();
			const eventUuids = eventData.map((item) => item.uuid).toString();
			set({ eventUuids });
			useNotificationStore.setState({ eventUuids });

			if (type === 'delete') {
				const { notificationsRead } = useNotificationStore.getState();
				Store.save(Constants.storageNotificationsRead, notificationsRead);
			}
		},

		fetchEvent: async (eventUuid: string, silent = false) => {
			// Cache-first: load the locally stored event so its content is navigable offline OR when the server is
			// unreachable (NetInfo can report "connected" while the API is down).
			await useEventStore.getState().fetchEventFromStorage(eventUuid);
			// Then revalidate from the server when connected.
			if (useNavigationStore.getState().isConnected) {
				await useEventStore.getState().fetchEventFromApi(eventUuid, { silent });
			}
		},

		// Fetches a single page (gallery, detail, …) for an event.
		getPage: async ({ eventUuid, pageId }: { eventUuid: string; pageId: string | number }) => {
			const { data } = await ApiService.endpoints.events.getPage({ eventUuid, pageId });
			return data;
		},

		// Fetches the logged-in user's registration for an event (My ticket).
		getMySubmission: async ({ eventIdentifier, signal }: { eventIdentifier: string; signal?: AbortSignal }) => {
			const { data } = await ApiService.endpoints.submissions.getMine({ eventIdentifier, signal });
			return data;
		},

		// Refreshes all of the user's events (Home/Archive).
		refreshAllEvents: async () => {
			const isConnected = useNavigationStore.getState().isConnected;
			if (!isConnected) {
				return;
			}
			const { eventData } = get();
			for (const event of eventData) {
				// eslint-disable-next-line no-await-in-loop
				await useEventStore.getState().fetchEventFromApi(event.uuid, { silent: true, updateActiveView: false });
			}
		},

		fetchEventFromStorage: async (eventUuid: string) => {
			const response = await Store.getObject(EventsStoreKey);
			if (response !== null) {
				const event = _.find(response as ApiEvent[], ['uuid', eventUuid]);
				if (event) {
					useEventStore.getState().setTheme(event.theme.data);
					useEventStore.getState().setLogo(event.logo);
					const pages = _.values(event.pages);
					set((state) => ({
						activeEventUuid: event.uuid,
						activeEventView: [...pages],
						pages: _.union(state.pages ?? [], pages),
					}));
				}
			}
		},

		fetchEventFromApi: async (eventUuid: string, { silent = false, updateActiveView = true } = {}) => {
			// Only active-view (foreground) fetches take part in the staleness race; a background refresh keeps id 0
			// so it never invalidates a foreground fetch.
			const fetchId = updateActiveView ? ++latestActiveFetchId : 0;
			const eventFetchId = (latestFetchPerEvent[eventUuid] = (latestFetchPerEvent[eventUuid] ?? 0) + 1);
			const mutationAtStart = eventsStoreMutation;
			if (!silent) {
				set({ isLoading: true });
			}
			try {
				const { data } = await ApiService.endpoints.events.getEvent({ eventUuid, noCache: true });
				// A newer active-view fetch superseded this one: drop the stale result so it can't overwrite the newer
				// active view.
				if (updateActiveView && fetchId !== latestActiveFetchId) {
					return;
				}
				const pages = _.values(data.pages);

				if (updateActiveView) {
					useEventStore.getState().setLogo(data.logo);
					useEventStore.getState().setTheme(data.theme.data);
					set((state) => ({
						activeEventUuid: data.uuid,
						activeEventView: [...pages],
						pages: _.unionBy(pages, state.pages ?? [], 'id'),
					}));
				}

				// A newer fetch for this same event has already landed: its payload is the truth, so this older one must
				// neither overwrite the cache nor stamp it as fresh again.
				if (latestFetchPerEvent[eventUuid] !== eventFetchId) {
					return;
				}

				// Record freshness for every successful fetch (foreground, background or on-focus), so callers can skip a
				// refresh that would only repeat what we just received.
				set((state) => ({ lastFetchedAt: { ...state.lastFetchedAt, [data.uuid]: Date.now() } }));

				const responseStored = await Store.getObject(EventsStoreKey);
				// A delete landed while this fetch was in flight: its reduced list is authoritative, so skip the persist
				// to avoid writing the just-removed event back in.
				if (eventsStoreMutation !== mutationAtStart || latestFetchPerEvent[eventUuid] !== eventFetchId) {
					return;
				}
				const updated: ApiEvent[] = (responseStored as ApiEvent[]) || [];
				const index = _.findIndex(updated, { uuid: data.uuid });
				if (index !== -1) {
					updated.splice(index, 1, data);
				} else {
					updated.push(data);
				}
				Store.save(EventsStoreKey, updated);
				set({ eventData: [...updated] });
			} catch (err) {
				if (updateActiveView && fetchId !== latestActiveFetchId) {
					return;
				}
				console.error('Error fetching event:', err);
				// A failed fetch surfaces via the global API-error toast; the cached event stays visible.
			} finally {
				// Reset the loader for any non-silent fetch that still owns it; a superseded active-view fetch has
				// already returned above without touching it.
				if (!silent && (!updateActiveView || fetchId === latestActiveFetchId)) {
					set({ isLoading: false });
				}
			}
		},

		addEvent: async (eventCode: string) => {
			set({ isAddingEvent: true });
			try {
				const { data } = await ApiService.endpoints.events.redeem({ code: eventCode });

				if (handleRedeemRefusal(data.data, eventCode)) {
					set({ isAddingEvent: false });
					return;
				}

				const res: ApiEvent[] = ((await Store.getObject(EventsStoreKey)) as ApiEvent[]) || [];
				const exists = _.find(res, ['uuid', data.uuid]);

				if (!exists) {
					res.push(data);
					Store.set(EventsStoreKey, res);
					const ordered = _.orderBy(res, ['date'], ['desc']);
					const eventUuids = ordered.map((item) => item.uuid).toString();
					const allPages = ordered.map((event) => event.pages);
					const flatPages = _.flattenDeep(allPages) as ApiPage[];

					set({ eventData: ordered, eventUuids, allPages, pages: flatPages, isAddingEvent: false, pendingEventCode: null });
					useNotificationStore.setState({ eventUuids });

					// Re-sync the device's subscriptions now the new event is in the list.
					useNotificationStore.getState().registerDevice().catch(logError);

					useNotificationStore
						.getState()
						.getNotificationsByToken()
						.then(() => {
							useNotificationStore.getState().allOpened();
						})
						.catch(logError);

					// Link the event to the account when logged in (handled by useAuthenticationStore).
					authEventBus.emit(Constants.eventEventsAdded);
				} else {
					errorHandler(Translations.alertEventAlreadyAddedTitle, Translations.errorEventInList);
					set({ isAddingEvent: false });
				}
			} catch (e) {
				set({ isAddingEvent: false });

				// The server refuses with a 400 and a reason, so axios throws and the reason arrives here rather than in
				// the success path.
				const response = (e as { response?: { status?: number; data?: { data?: unknown } } })?.response;
				if (response?.status === 400 && handleRedeemRefusal(response.data?.data, eventCode)) {
					return;
				}

				// Explicit user action: show a clear failure message (the redeem call opts out of the global toast via
				// skipErrorToast, so this alert is the single feedback).
				errorHandler(Translations.errorDefaultSubject, Translations.errorAddEventFailed);
			}
		},

		// Read-only check whether an event with this code exists, without adding it (redeem would attach the user
		// server-side).
		eventExists: async (code: string): Promise<boolean> => {
			const { data } = await ApiService.endpoints.events.checkCode({ code });
			return Number(data) > 0;
		},

		deleteEvent: (eventCode: string, eventUuid: string) => {
			try {
				useAnalyticsStore.getState().eventDeleted(eventUuid);

				const updatedEventData = get().eventData.filter((event) => event.code !== eventCode);
				// Invalidate any in-flight refresh before persisting so it cannot resurrect this event.
				eventsStoreMutation++;
				set({ eventData: updatedEventData });
				useEventStore.getState().setEventUuids('delete');
				Store.save(EventsStoreKey, updatedEventData);

				// Re-sync the device's subscriptions with its now-reduced event list.
				useNotificationStore.getState().registerDevice().catch(logError);

				authEventBus.emit(Constants.eventEventsRemoved, eventUuid);
			} catch (err) {
				console.error('Delete event error:', err);
				errorHandler(Translations.errorDefaultSubject, Translations.errorDeleteEvent);
			}
		},

		validateEvents: () => {
			const { eventData } = get();
			const eventUuids = eventData.map((event) => event.uuid);

			ApiService.endpoints.events
				.validateIds({ ids: eventUuids })
				.then(({ data }) => {
					const validEvents = eventData.filter((event) => data.valid.includes(event.uuid));
					// Invalidate any in-flight refresh so a dropped (invalid) event can't be resurrected.
					eventsStoreMutation++;
					set({ eventData: validEvents });
					useEventStore.getState().setEventUuids();

					// Re-sync the device's subscriptions with the validated event list.
					useNotificationStore.getState().registerDevice().catch(logError);

					Store.save(EventsStoreKey, validEvents);
				})
				.catch(logError);
		},

		deleteSelection: () => {
			const { selectedEvents } = get();
			let updatedData = get().eventData;

			selectedEvents.forEach((eventUuid: string) => {
				updatedData = updatedData.filter((event) => event.uuid !== eventUuid);
			});

			// Invalidate any in-flight refresh before persisting so it cannot resurrect a removed event.
			eventsStoreMutation++;
			Store.save(EventsStoreKey, updatedData);

			authEventBus.emit(Constants.eventEventsRemoved, selectedEvents);

			set({ eventData: updatedData, selectedEvents: [] });
			useEventStore.getState().setEventUuids('delete');

			// Re-sync after removing the selected events.
			useNotificationStore.getState().registerDevice().catch(logError);
		},

		clearSelection: () => set({ selectedEvents: [] }),

		getPageByID: (pageIDs: string[]) => {
			const pages = get().pages;
			const holderPage = _.find(pages, ['id', parseInt(pageIDs[0]!)]);

			if (holderPage?.event_uuid) {
				useAnalyticsStore.getState().pageOpened(holderPage.event_uuid, pageIDs.slice(-1)[0]);

				switch (pageIDs.length) {
					case 1:
						return holderPage;
					case 2:
						return (_.find(holderPage.children, ['id', parseInt(pageIDs[1]!)]) as ApiPage) ?? null;
					case 3: {
						const childPage = _.find(holderPage.children, ['id', parseInt(pageIDs[1]!)]);
						return childPage ? ((_.find(childPage.children, ['id', parseInt(pageIDs[2]!)]) as ApiPage) ?? null) : null;
					}
					default:
						return null;
				}
			}
			return null;
		},

		updateEvents: async (events: unknown[]) => {
			// Flag loading so the Home screen can show a spinner while account-linked events are fetched after login
			// (this runs un-awaited from getProfile, so it is still in flight on Home).
			set({ isLoading: true });
			try {
				const { eventData } = get();
				const serverUuids = new Set((events as Array<{ uuid: string }>).map((e) => e.uuid));
				const localUuids = new Set(eventData.map((e) => e.uuid));

				const kept = eventData.filter((e) => serverUuids.has(e.uuid));
				const missingUuids = Array.from(serverUuids).filter((uuid) => !localUuids.has(uuid));

				const fetched: ApiEvent[] = [];
				for (const uuid of missingUuids) {
					try {
						const { data } = await ApiService.endpoints.events.getEvent({ eventUuid: uuid, noCache: true });
						fetched.push(data);
					} catch (error) {
						console.error('Failed to fetch linked event:', uuid, error);
					}
				}

				const merged = _.orderBy([...kept, ...fetched], ['date'], ['desc']);
				Store.save(EventsStoreKey, merged);
				set({ eventData: merged });
				useEventStore.getState().setEventUuids();
			} finally {
				set({ isLoading: false });
			}
		},
	})),
);
